import { useEffect, useRef, useState } from "react";
import { CreditReturnsViewModel } from "../../viewmodels/creditReturns/CreditReturnsViewModel";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";

import { connect } from "react-redux";

import * as actions from "../../store/actions/index";

import {
  formatDateWithTime,
  withRouter,
} from "../../shared/utility";

import EmptyModal from "../../components/modals/EmptyModal";
import SlideOver from "../../components/slideOver/SlideOver";
import SimpleNotification from "../../components/SimpleNotification";

import { Link } from "react-router-dom";

import Pills, { PillType } from "../../components/controls/Pills";
import FilterDropdown from "../../components/filterDropdown/FilterDropdown";

import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import InfiniteScroll from "react-infinite-scroll-component";

function CreditReturns(props) {
  const { logout } = props;
  const {
    emptyModal,
    getCreditReturns,
    loading,
    creditReturns,
    setSlideOver,
    slideOver,
    setEmptyModelOpen,
    setShowSimpleNotification,
    showSimpleNotification,
    filters,
    defaultFilters,
    showFilters,
    setShowFilters,
    filterComponents,
    resetFilters,
    getCustomers,
    printShipments,
    totalCreditReturns,
  } = CreditReturnsViewModel(logout);

  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    getCreditReturns(null, 1, defaultFilters);
    getCustomers();
  }, []);

  const searchRef = useRef();

  const handleSearch = () => {
    getCreditReturns(searchRef.current.value, 1, filters);
  };

  const handleLoadMoreData = () => {
    setTimeout(() => {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);

      getCreditReturns(searchRef.current.value, nextPage, filters);
    }, 250);
  };

  function bodyDateFormatted(rowData) {
    return <div>{formatDateWithTime(rowData.created_date)}</div>;
  }

  function bodyStatus(rowData) {
    const content =
      rowData.status === 1
        ? { text: "Closed", type: PillType.Success }
        : { text: "Open", type: PillType.DarkYellow };

    return <Pills content={content} />;
  }

  function bodyMoreActions(rowData) {
    return (
      <Link
        className="rounded-md bg-main-purple px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
        to={`/credit-returns/${rowData.id}`}
      >
        View
      </Link>
    );
  }

  return (
    <div>
      <SimpleNotification
        setShow={setShowSimpleNotification}
        showSimpleNotification={showSimpleNotification}
      />
      <div className="mt-2">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="sm:flex sm:items-center px-4 py-5 sm:px-4 card-custom">
            <div className="sm:flex-auto">
              <h1 className="text-base font-semibold leading-6 text-gray-900">
                Credit Returns
              </h1>
              <div className="mt-5 block sm:flex rounded-md space-y-4 sm:space-y-0">
                <div className="relative flex items-stretch flex-grow focus-within:z-10">
                  <input
                    type="search"
                    name="searchOrder"
                    id="searchOrder"
                    className="block w-full rounded-lg rounded-l-md pl-2 sm:text-sm border border-gray-300 rounded-tr-none rounded-br-none"
                    placeholder="Search by customer or po number"
                    ref={searchRef}
                    onKeyDown={(e) =>
                      e.key === "Enter" ? handleSearch() : null
                    }
                  />
                  <button
                    type="button"
                    className="-ml-px relative inline-flex items-center space-x-2 px-4 py-2 border border-gray-300 text-sm font-medium rounded-r-md text-gray-700 bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-1"
                    onClick={() => handleSearch()}
                  >
                    <MagnifyingGlassIcon
                      className="h-5 w-5 text-gray-400"
                      aria-hidden="true"
                    />
                  </button>
                </div>

                <div className="flex">
                  <FilterDropdown
                    actionCall={() =>
                      getCreditReturns(searchRef.current.value, 1, filters)
                    }
                    filterComponents={filterComponents}
                    resetFilters={() => {
                      resetFilters();
                    }}
                    showFilters={showFilters}
                    setShowFilters={setShowFilters}
                  />

                  <div>
                    <button
                      className="ml-2 rounded-md bg-yellow-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-yellow-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-yellow-600"
                      onClick={printShipments}
                    >
                      Print
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-8 flow-root">
            <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                {loading ? (
                  <div>
                    <div className="border h-10 shadow rounded-md p-4 w-full mx-auto">
                      <div className="animate-pulse flex space-x-4">
                        Loading
                      </div>
                    </div>
                  </div>
                ) : (
                  <InfiniteScroll
                    dataLength={creditReturns.length}
                    next={handleLoadMoreData}
                    hasMore={creditReturns.length < totalCreditReturns}
                    loader={<p className="mt-2 text-center">Loading...</p>}
                    scrollThreshold={0.95} // or "200px"
                    endMessage={
                      <p className="mt-2 text-center">
                        No more shipments to load
                      </p>
                    }
                  >
                    <div className="overflow-hidden card-custom">
                      <DataTable
                        value={creditReturns}
                        dataKey="unique_id"
                        tableClassName="min-w-full bg-white divide-y divide-gray-300 text-sm"
                        cellClassName="p-2 border-1-bottom-gray text-sm"
                      >
                        <Column header="Name" field="name" />
                        <Column header="Customer" field="customer_name" />
                        <Column header="Date" body={bodyDateFormatted} />
                        <Column header="Status" body={bodyStatus} />
                        <Column header="Created by" field="created_by_name" />
                        <Column header="" body={bodyMoreActions} />
                      </DataTable>
                    </div>
                  </InfiniteScroll>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <EmptyModal
        childComponent={emptyModal.childComponent}
        open={emptyModal.open}
        setOpen={setEmptyModelOpen}
      />
      <SlideOver
        childComponent={slideOver.childComponent}
        slideOver={slideOver}
        setSlideOver={setSlideOver}
      />
    </div>
  );
}

const mapStateToProps = (state) => {
  return {};
};

const mapDispatchToProps = (dispatch) => {
  return {
    logout: () => dispatch(actions.logout()),
  };
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(CreditReturns)
);
